// ── Render the coach's own words with their parsed claims lit up ─────
// Pure and dependency-free, same discipline as parse-task.js: no DOM, no
// module imports, just text in / HTML string out. That is what makes it
// testable in the `node` vitest environment (see vitest.config.ts) with no
// jsdom, and what keeps the walk in one place instead of drifting between a
// tested copy and a real one.
//
// parseTask's spans are SORTED by start, but not guaranteed DISJOINT —
// findClient, findDue and findType each scan independently, so a type
// keyword sitting inside a client name ("call @Amanda Block about renewal")
// produces a nested/overlapping span. A walk that assumes disjoint spans
// either double-emits the overlapped text or, when the inner span ends
// before the outer one, rewinds `cursor` and re-emits the whole tail of the
// sentence on top of what was already written.
//
// Fix: skip any span whose end has already been passed by the cursor (fully
// swallowed by a span already emitted), and clamp a partially-overlapping
// span's start to the cursor before emitting it. That keeps cursor
// monotonically non-decreasing no matter how spans overlap, so the walk can
// never double-emit or rewind. Because spans are sorted by start, this also
// means the first (outermost) span wins for any character it covers — the
// client-name claim is the more specific one, so that is the right call.
//
// THE invariant this exists to guarantee: strip every tag from the output
// and you get the input text back, unchanged, character for character. That
// is what makes it impossible for a coach to see words they never typed.
//
// `escape` is taken as a parameter rather than imported from utils.js (the
// obvious alternative: import escHtml directly). Passing it in keeps this
// module at zero imports — same posture as parse-task.js — and means the
// only thing under test is the walk itself; the caller (coach-tasks.js,
// which already imports escHtml for the rest of the composer) supplies the
// exact same escaper used everywhere else in the render, so there is no
// second copy of escaping logic that could quietly drift from escHtml.
export function highlightSpans(text, spans, offKinds, escape) {
  const src = String(text ?? '');
  const off = offKinds || { has: () => false };
  let out = '';
  let cursor = 0;
  for (const s of spans || []) {
    if (s.end <= cursor) continue; // fully covered by a span already emitted — skip, never rewind
    const start = Math.max(s.start, cursor); // clamp a partial overlap to where we already are
    out += escape(src.slice(cursor, start));
    const isOff = typeof off.has === 'function' ? off.has(s.kind) : false;
    out += `<span class="ctk-tk ctk-tk-${s.kind}${isOff ? ' is-off' : ''}" onclick="window.coachTasks.qaToggleSpan('${s.kind}')" title="Click to un-set">${escape(src.slice(start, s.end))}</span>`;
    cursor = s.end;
  }
  out += escape(src.slice(cursor));
  return out;
}
